import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ExternalLink, Phone } from "lucide-react";
import UserAvatar from "components/user-avatar/user-avatar";
import { OnlineUser } from "services/presence.service";
import { startDm } from "services/chat.service";
import { RootState, AppDispatch } from "configureStore";
import {
  openConversation,
  openMiniChat,
  setActiveTab,
  upsertConversation,
} from "features/chat/chat.slice";
import { setExpanded } from "./presence.slice";
import { useCalls } from "features/calls/calls-provider";
import useIsPhone from "features/files/use-is-phone";
import { formatLastSeen } from "./format-last-seen";
import { toast } from "sonner";

interface Props {
  companyId: string;
  expanded: boolean;
  avatarSize: number;
  online: OnlineUser[];
  offline: OnlineUser[];
}

const OnlineTab: React.FC<Props> = ({
  companyId,
  expanded,
  avatarSize,
  online,
  offline,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const lang = ((i18n as any).language || "en") as "lt" | "en";

  // Build a userId → unreadCount map from DM conversations so we can render
  // a red badge on the corresponding avatar in the online/offline list.
  // Especially valuable in collapsed mode (64px) where the chevron-level
  // total unread doesn't tell you WHO the messages are from.
  const conversations = useSelector(
    (state: RootState) => state.chatReducer.conversations
  );
  const unreadByUser = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of conversations) {
      if (c.type !== "dm" || !c.otherParticipant) continue;
      if (c.unreadCount > 0) m.set(c.otherParticipant.id, c.unreadCount);
    }
    return m;
  }, [conversations]);

  const { enabled: callsEnabled, busyConversationId, startCall } = useCalls();
  // The pop-out mini window is wider than a phone screen, so a phone has only the sheet.
  const isPhone = useIsPhone();

  /** The DM with this colleague, created on the spot if there is none. Calling
   *  someone needs one too: a call happens inside a conversation's room. */
  const ensureDm = async (user: OnlineUser): Promise<string | null> => {
    const existing = conversations.find(
      (c) => c.type === "dm" && c.otherParticipant?.id === user.id
    );
    if (existing) return existing.id;
    try {
      const convo = await startDm(companyId, user.id);
      dispatch(upsertConversation(convo));
      return convo.id;
    } catch {
      toast.error(t("error"));
      return null;
    }
  };

  /** Clicking a colleague opens the conversation with them — the rail expands
   *  if it was collapsed, so the chat (and the call button in its header) is
   *  right there. Who they ARE is one more click away, on their name in the
   *  chat header. */
  const handleOpenChat = async (user: OnlineUser) => {
    dispatch(setExpanded(true));
    dispatch(setActiveTab("chats"));
    const conversationId = await ensureDm(user);
    if (conversationId) dispatch(openConversation({ companyId, conversationId }));
  };

  const handleCall = async (user: OnlineUser) => {
    if (busyConversationId) return;
    const conversationId = await ensureDm(user);
    if (!conversationId) return;
    startCall(companyId, conversationId, { userId: String(user.id), name: user.name });
  };

  const handlePopOut = async (user: OnlineUser) => {
    const conversationId = await ensureDm(user);
    if (!conversationId) return;
    dispatch(
      openMiniChat({
        conversationId,
        companyId,
        title: user.name,
        avatarFileId: user.avatarFileId,
        conversationType: "dm",
        forceExpand: true,
      })
    );
  };

  /** The per-row buttons. Hidden while the rail is collapsed: at 64px there is
   *  only room for the avatar, and clicking it expands the rail anyway. */
  const rowActions = (user: OnlineUser) =>
    expanded ? (
      <>
        {callsEnabled && (
          <button
            type="button"
            className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white disabled:opacity-40 max-lg:flex max-lg:min-h-11 max-lg:min-w-11 max-lg:items-center max-lg:justify-center"
            // already talking to someone: ringing them would only be turned
            // away as busy, so the button says so instead of pretending
            disabled={!!busyConversationId || !!user.inCall}
            onClick={(e) => {
              e.stopPropagation();
              void handleCall(user);
            }}
            title={user.inCall ? t("call_on_a_call") : t("call_start")}
            aria-label={user.inCall ? t("call_on_a_call") : t("call_start")}
          >
            <Phone size={14} />
          </button>
        )}
        {!isPhone && (
          <button
            type="button"
            className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              void handlePopOut(user);
            }}
            title={t("pop_out_chat")}
          >
            <ExternalLink size={14} />
          </button>
        )}
      </>
    ) : null;

  return (
    <>
      {online.length === 0 && expanded && (
        <div className="px-3 py-2 text-sm text-gray-500">{t("no_one_online")}</div>
      )}
      {online.map((u) => (
        <div
          key={u.id}
          onClick={() => void handleOpenChat(u)}
          className="px-3 py-2 flex items-center gap-3 hover:bg-gray-800 cursor-pointer group"
        >
          <UserAvatar
            companyId={companyId}
            user={{ id: u.id, name: u.name, avatarFileId: u.avatarFileId }}
            size={avatarSize}
            online
            unread={unreadByUser.get(u.id) || 0}
          />
          {expanded && (
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{u.name}</div>
              {u.inCall ? (
                <div className="text-xs text-amber-400 flex items-center gap-1 truncate">
                  <Phone size={11} className="shrink-0" />
                  {t("call_on_a_call")}
                </div>
              ) : (
                u.position && (
                  <div className="text-xs text-gray-400 truncate">{u.position}</div>
                )
              )}
            </div>
          )}
          {rowActions(u)}
        </div>
      ))}

      {offline.length > 0 && expanded && (
        <div className="px-3 pt-4 pb-1 text-xs uppercase tracking-wide text-gray-500">
          {t("offline_section_title")}
        </div>
      )}
      {expanded &&
        offline.map((u) => (
          <div
            key={u.id}
            onClick={() => void handleOpenChat(u)}
            className="px-3 py-2 flex items-center gap-3 hover:bg-gray-800 cursor-pointer group"
          >
            <div className="opacity-60 flex-1 flex items-center gap-3 min-w-0">
              <UserAvatar
                companyId={companyId}
                user={{ id: u.id, name: u.name, avatarFileId: u.avatarFileId }}
                size={avatarSize}
                unread={unreadByUser.get(u.id) || 0}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{u.name}</div>
                {u.inCall ? (
                  <div className="text-xs text-amber-400 flex items-center gap-1 truncate">
                    <Phone size={11} className="shrink-0" />
                    {t("call_on_a_call")}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 truncate">
                    {u.position ? `${u.position} · ` : ""}
                    {formatLastSeen(u.lastSeenAt, t, lang)}
                  </div>
                )}
              </div>
            </div>
            {rowActions(u)}
          </div>
        ))}
    </>
  );
};

export default OnlineTab;
